import { useEffect, useMemo, useState } from 'react'
import { Loader2, Lock, Target, Users } from 'lucide-react'
import { useDirty } from '@/hooks/useDirty'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { FormField } from '@/components/FormField'
import { cn } from '@/lib/cn'
import { ApiError } from '@/lib/api'
import { RoleMemberSharePicker } from '@/components/RoleMemberSharePicker'
import { useAuth } from '@/auth/useAuth'
import { useCreateWorkflow, useUpdateWorkflow } from '@/features/workflows/queries'
import { workflowsApi } from '@/features/workflows/api'
import type {
  Visibility,
  Workflow,
  WorkflowPayload,
  WorkflowStep,
} from '@/features/workflows/types'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  workflow: Workflow | null
  onSaved?: () => void
}

type Freq = 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'

const COMMON_TZ = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Australia/Sydney',
]

const PLACEHOLDER_DOMAINS = new Set([
  'example.com', 'example.org', 'example.net', 'test.com', 'domain.com', 'yourcompany.com',
])

function cleanEmails(arr: string[]): string[] {
  return arr
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((e) => {
      const at = e.indexOf('@')
      if (at <= 0) return false
      return !PLACEHOLDER_DOMAINS.has(e.slice(at + 1).toLowerCase())
    })
}

function pad(n: number | string): string {
  const s = String(n)
  return s.length === 1 ? `0${s}` : s
}

function parseCron(cron: string | null | undefined): {
  freq: Freq
  hour: number
  minute: number
  dom: number
  dow: number
  month: number
} {
  const def = { freq: 'monthly' as Freq, hour: 9, minute: 0, dom: 3, dow: 1, month: 1 }
  if (!cron) return def
  const parts = cron.trim().split(/\s+/)
  if (parts.length !== 5) return def
  const [min, hr, dom, mon, dow] = parts
  const hour = parseInt(hr ?? '9', 10) || 0
  const minute = parseInt(min ?? '0', 10) || 0

  if (dow === '1-5' && dom === '*' && mon === '*') return { ...def, freq: 'weekdays', hour, minute }
  if (dow !== '*' && dom === '*' && mon === '*')
    return { ...def, freq: 'weekly', hour, minute, dow: parseInt(dow ?? '1', 10) }
  if (dom !== '*' && dow === '*' && mon === '1,4,7,10')
    return { ...def, freq: 'quarterly', hour, minute, dom: parseInt(dom ?? '1', 10) }
  if (dom !== '*' && dow === '*' && /^\d{1,2}$/.test(mon ?? ''))
    return {
      ...def,
      freq: 'yearly',
      hour,
      minute,
      dom: parseInt(dom ?? '1', 10),
      month: parseInt(mon ?? '1', 10),
    }
  if (dom !== '*' && dow === '*' && mon === '*')
    return { ...def, freq: 'monthly', hour, minute, dom: parseInt(dom ?? '1', 10) }
  if (dom === '*' && dow === '*' && mon === '*')
    return { ...def, freq: 'daily', hour, minute }
  return def
}

function buildCron(freq: Freq, hour: number, minute: number, dom: number, dow: number, month: number): string {
  if (freq === 'daily') return `${minute} ${hour} * * *`
  if (freq === 'weekdays') return `${minute} ${hour} * * 1-5`
  if (freq === 'weekly') return `${minute} ${hour} * * ${dow}`
  if (freq === 'monthly') return `${minute} ${hour} ${dom} * *`
  if (freq === 'quarterly') return `${minute} ${hour} ${dom} 1,4,7,10 *`
  if (freq === 'yearly') return `${minute} ${hour} ${dom} ${month} *`
  return ''
}

function describeCron(cron: string): string {
  if (!cron) return ''
  const parts = cron.split(/\s+/)
  if (parts.length !== 5) return cron
  const [min, hr, dom, mon, dow] = parts
  const t = `${pad(hr ?? 0)}:${pad(min ?? 0)}`
  const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  if (dow === '1-5' && dom === '*' && mon === '*') return `Every weekday at ${t}`
  if (dow !== '*' && dom === '*' && mon === '*') return `Every ${DAYS[parseInt(dow ?? '1', 10)] ?? dow} at ${t}`
  if (dom !== '*' && dow === '*' && mon === '1,4,7,10') return `Every quarter on day ${dom} at ${t}`
  if (dom !== '*' && dow === '*' && /^\d{1,2}$/.test(mon ?? ''))
    return `Every year on ${MONTHS[parseInt(mon ?? '1', 10)]} ${dom} at ${t}`
  if (dom !== '*' && dow === '*' && mon === '*') return `Every month on day ${dom} at ${t}`
  if (dom === '*' && dow === '*' && mon === '*') return `Every day at ${t}`
  return cron
}

export function WorkflowFormModal({ open, onOpenChange, workflow, onSaved }: Props) {
  const createM = useCreateWorkflow()
  const updateM = useUpdateWorkflow()
  const busy = createM.isPending || updateM.isPending

  const [tab, setTab] = useState<'prompt' | 'form'>('prompt')
  const [prompt, setPrompt] = useState('')
  const [parseStatus, setParseStatus] = useState('')
  const [parseLoading, setParseLoading] = useState(false)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [freq, setFreq] = useState<Freq>('monthly')
  const [hour, setHour] = useState(9)
  const [minute, setMinute] = useState(0)
  const [dom, setDom] = useState(3)
  const [dow, setDow] = useState(1)
  const [month, setMonth] = useState(1)
  const defaultTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    } catch {
      return 'UTC'
    }
  }, [])
  const [tz, setTz] = useState(defaultTz)
  const [reportType, setReportType] = useState('generate_income_statement')
  const [dateRange, setDateRange] = useState('previous_month')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [emails, setEmails] = useState('')
  const [notify, setNotify] = useState(false)
  const [visibility, setVisibility] = useState<Visibility>('private')
  const [shareValue, setShareValue] = useState<{ sharedRoleIds: number[]; sharedUserIds: number[] }>({
    sharedRoleIds: [],
    sharedUserIds: [],
  })
  const [error, setError] = useState<string | null>(null)
  const { user } = useAuth()
  const currentUserId = user?.userId ? Number(user.userId) : null

  // Dirty-check the modal. Snapshot every field that becomes part of
  // the WorkflowPayload (cron pieces, report config, recipients, share
  // list, visibility). For NEW workflows the baseline mirrors the
  // empty-state branch in the open-effect below, so any keystroke or
  // field change unlocks Save.
  const formSnapshot = useMemo(
    () => ({
      name: name.trim(),
      description: description.trim(),
      freq, hour, minute, dom, dow, month, tz,
      reportType,
      dateRange,
      startDate: dateRange === 'custom' ? startDate : '',
      endDate: dateRange === 'custom' ? endDate : '',
      emails: cleanEmails(emails.split(/[\n,;]/)).sort(),
      notify,
      visibility,
      sharedRoleIds: [...shareValue.sharedRoleIds].sort((a, b) => a - b),
      sharedUserIds: [...shareValue.sharedUserIds].sort((a, b) => a - b),
    }),
    [
      name, description, freq, hour, minute, dom, dow, month, tz,
      reportType, dateRange, startDate, endDate, emails, notify, visibility, shareValue,
    ],
  )
  const baselineSnapshot = useMemo(() => {
    if (!workflow) {
      return {
        name: '',
        description: '',
        freq: 'monthly' as Freq,
        hour: 9, minute: 0, dom: 3, dow: 1, month: 1,
        tz: defaultTz,
        reportType: 'generate_income_statement',
        dateRange: 'previous_month',
        startDate: '',
        endDate: '',
        emails: [] as string[],
        notify: false,
        visibility: 'private' as Visibility,
        sharedRoleIds: [] as number[],
        sharedUserIds: [] as number[],
      }
    }
    const c = parseCron(workflow.cron_expression)
    const steps = Array.isArray(workflow.steps)
      ? workflow.steps
      : typeof workflow.steps === 'string'
        ? (() => {
            try { return JSON.parse(workflow.steps) as WorkflowStep[] } catch { return [] }
          })()
        : []
    const reportStep = steps.find((s) => (s.step_type || '').startsWith('generate_'))
    const emailStep = steps.find((s) => s.step_type === 'send_email')
    const notifStep = steps.find((s) => s.step_type === 'create_notification')
    const cfg = (reportStep?.step_config ?? {}) as { date_range?: string; start_date?: string; end_date?: string }
    const emailCfg = (emailStep?.step_config ?? {}) as { to?: string[] }
    const dr = cfg.date_range ?? 'previous_month'
    return {
      name: workflow.name ?? '',
      description: workflow.description ?? '',
      freq: c.freq, hour: c.hour, minute: c.minute, dom: c.dom, dow: c.dow, month: c.month,
      tz: workflow.timezone || defaultTz,
      reportType: reportStep?.step_type ?? 'generate_income_statement',
      dateRange: dr,
      startDate: dr === 'custom' ? (cfg.start_date ?? '') : '',
      endDate: dr === 'custom' ? (cfg.end_date ?? '') : '',
      emails: cleanEmails(Array.isArray(emailCfg.to) ? emailCfg.to : []).sort(),
      notify: !!notifStep,
      visibility: workflow.visibility ?? 'private',
      sharedRoleIds: (workflow.shared_roles ?? []).map((r) => r.role_id).sort((a, b) => a - b),
      sharedUserIds: (workflow.shared_users ?? []).map((u) => u.user_id).sort((a, b) => a - b),
    }
  }, [workflow, defaultTz])
  const dirty = useDirty(formSnapshot, baselineSnapshot)

  useEffect(() => {
    if (!open) return
    setError(null)
    setPrompt('')
    setParseStatus('')

    if (workflow) {
      setTab('form')
      setName(workflow.name ?? '')
      setDescription(workflow.description ?? '')
      const c = parseCron(workflow.cron_expression)
      setFreq(c.freq)
      setHour(c.hour)
      setMinute(c.minute)
      setDom(c.dom)
      setDow(c.dow)
      setMonth(c.month)
      setTz(workflow.timezone || defaultTz)
      const steps = Array.isArray(workflow.steps)
        ? workflow.steps
        : typeof workflow.steps === 'string'
          ? (() => {
              try { return JSON.parse(workflow.steps) as WorkflowStep[] } catch { return [] }
            })()
          : []
      const reportStep = steps.find((s) => (s.step_type || '').startsWith('generate_'))
      const emailStep = steps.find((s) => s.step_type === 'send_email')
      const notifStep = steps.find((s) => s.step_type === 'create_notification')
      setReportType(reportStep?.step_type ?? 'generate_income_statement')
      const cfg = (reportStep?.step_config ?? {}) as {
        date_range?: string; start_date?: string; end_date?: string
      }
      setDateRange(cfg.date_range ?? 'previous_month')
      setStartDate(cfg.start_date ?? '')
      setEndDate(cfg.end_date ?? '')
      const emailCfg = (emailStep?.step_config ?? {}) as { to?: string[] }
      setEmails(cleanEmails(Array.isArray(emailCfg.to) ? emailCfg.to : []).join(', '))
      setNotify(!!notifStep)
      setVisibility(workflow.visibility ?? 'private')
      setShareValue({
        sharedRoleIds: (workflow.shared_roles ?? []).map((r) => r.role_id),
        sharedUserIds: (workflow.shared_users ?? []).map((u) => u.user_id),
      })
    } else {
      setTab('prompt')
      setName('')
      setDescription('')
      setFreq('monthly')
      setHour(9)
      setMinute(0)
      setDom(3)
      setDow(1)
      setMonth(1)
      setTz(defaultTz)
      setReportType('generate_income_statement')
      setDateRange('previous_month')
      setStartDate('')
      setEndDate('')
      setEmails('')
      setNotify(false)
      setVisibility('private')
      setShareValue({ sharedRoleIds: [], sharedUserIds: [] })
    }
  }, [open, workflow, defaultTz])

  const cron = useMemo(
    () => buildCron(freq, hour, minute, dom, dow, month),
    [freq, hour, minute, dom, dow, month],
  )

  const applyDraft = (d: {
    name?: string
    description?: string
    cron_expression?: string
    timezone?: string
    steps?: WorkflowStep[]
  }) => {
    if (d.name) setName(d.name)
    if (d.description) setDescription(d.description)
    if (d.cron_expression) {
      const c = parseCron(d.cron_expression)
      setFreq(c.freq)
      setHour(c.hour)
      setMinute(c.minute)
      setDom(c.dom)
      setDow(c.dow)
      setMonth(c.month)
    }
    if (d.timezone) setTz(d.timezone)
    const steps = d.steps ?? []
    const reportStep = steps.find((s) => (s.step_type || '').startsWith('generate_'))
    const emailStep = steps.find((s) => s.step_type === 'send_email')
    const notifStep = steps.find((s) => s.step_type === 'create_notification')
    if (reportStep?.step_type) setReportType(reportStep.step_type)
    const cfg = (reportStep?.step_config ?? {}) as {
      date_range?: string; start_date?: string; end_date?: string
    }
    if (cfg.date_range) setDateRange(cfg.date_range)
    if (cfg.start_date) setStartDate(cfg.start_date)
    if (cfg.end_date) setEndDate(cfg.end_date)
    const emailCfg = (emailStep?.step_config ?? {}) as { to?: string[] }
    if (Array.isArray(emailCfg.to)) setEmails(cleanEmails(emailCfg.to).join(', '))
    setNotify(!!notifStep)
  }

  const parsePrompt = async (): Promise<boolean> => {
    const trimmed = prompt.trim()
    if (!trimmed) {
      setParseStatus('Type a description first.')
      return false
    }
    setParseStatus('Parsing…')
    setParseLoading(true)
    try {
      const data = await workflowsApi.parse(trimmed)
      const draft = data.draft ?? {}
      applyDraft(draft)
      setParseStatus(
        data.needs?.length
          ? `Missing: ${data.needs.join(', ')} - fill them in the form.`
          : 'Parsed. Review the form below.',
      )
      setTab('form')
      return true
    } catch (err) {
      setParseStatus('Parse failed: ' + messageFor(err))
      return false
    } finally {
      setParseLoading(false)
    }
  }

  const buildSteps = (): WorkflowStep[] => {
    const steps: WorkflowStep[] = []
    const reportConfig: Record<string, unknown> = { date_range: dateRange }
    if (dateRange === 'custom') {
      reportConfig.start_date = startDate
      reportConfig.end_date = endDate
    }
    steps.push({ step_type: reportType, step_config: reportConfig })

    const list = cleanEmails(emails.split(/[\n,;]/))
    if (list.length) steps.push({ step_type: 'send_email', step_config: { to: list } })
    if (notify) steps.push({ step_type: 'create_notification', step_config: {} })
    return steps
  }

  const submit = async (status: 'active' | 'paused') => {
    setError(null)
    if (tab !== 'form') {
      setError('Parse your prompt first (or switch to the form tab) before saving.')
      return
    }
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    if (!cron) {
      setError('Schedule is required.')
      return
    }
    if (dateRange === 'custom') {
      if (!startDate || !endDate) {
        setError('Pick both a From and a To date for the custom period.')
        return
      }
      if (startDate > endDate) {
        setError('The From date must be on or before the To date.')
        return
      }
    }
    const steps = buildSteps()
    const hasEmail = steps.some((s) => s.step_type === 'send_email')
    const hasNotify = steps.some((s) => s.step_type === 'create_notification')
    if (!hasEmail && !hasNotify) {
      setError('Add at least one email recipient or enable in-app notifications.')
      return
    }
    if (
      visibility === 'shared' &&
      shareValue.sharedRoleIds.length === 0 &&
      shareValue.sharedUserIds.length === 0
    ) {
      setError('Pick at least one role or member, or switch to "Only me" / "Whole team".')
      return
    }

    const payload: WorkflowPayload = {
      name: name.trim(),
      description: description.trim(),
      trigger_type: 'schedule',
      cron_expression: cron,
      timezone: tz || 'UTC',
      status: workflow ? undefined : status,
      visibility,
      shared_user_ids: visibility === 'shared' ? shareValue.sharedUserIds : [],
      shared_role_ids: visibility === 'shared' ? shareValue.sharedRoleIds : [],
      steps,
    }

    try {
      if (workflow) {
        await updateM.mutateAsync({ id: workflow.id, body: payload })
      } else {
        await createM.mutateAsync(payload)
      }
      onSaved?.()
      onOpenChange(false)
    } catch (err) {
      // 402 = AI Gateway limit. Defer to the global LimitReachedDialog.
      if (err instanceof ApiError && err.status === 402) return
      setError(messageFor(err))
    }
  }

  const onSaveDraft = async () => {
    if (tab === 'prompt') {
      const ok = await parsePrompt()
      if (!ok) return
    }
    await submit('paused')
  }

  const title = workflow ? 'Edit Workflow' : 'New Workflow'
  const subtitle = workflow
    ? 'Update the schedule, delivery, or who can see this workflow.'
    : 'Describe what you want in plain English, or fill in the form manually.'

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={subtitle}
      className="max-w-2xl"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          {!workflow ? (
            <Button variant="secondary" onClick={() => void onSaveDraft()} disabled={!dirty || busy}>
              Save as Draft
            </Button>
          ) : null}
          <Button
            onClick={() => void submit('active')}
            disabled={!dirty || busy || tab !== 'form'}
            title={tab !== 'form' ? 'Parse your prompt or switch to the form tab.' : ''}
          >
            {workflow ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-4 pt-2">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {!workflow ? (
          <div className="flex gap-0 border-b">
            <TabBtn active={tab === 'prompt'} onClick={() => setTab('prompt')}>
              From prompt
            </TabBtn>
            <TabBtn active={tab === 'form'} onClick={() => setTab('form')}>
              From form
            </TabBtn>
          </div>
        ) : null}

        {tab === 'prompt' ? (
          <div className="space-y-3">
            <FormField
              label="Describe what you want in plain English"
              control={
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.currentTarget.value)}
                  placeholder="e.g. Email the income statement on the 3rd each month at 9 AM"
                  className="min-h-[120px]"
                />
              }
            />
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">{parseStatus}</p>
              <Button size="sm" onClick={() => void parsePrompt()} disabled={parseLoading}>
                {parseLoading ? <Loader2 className="size-4 animate-spin" /> : null}
                Parse →
              </Button>
            </div>
          </div>
        ) : null}

        {tab === 'form' ? (
          <div className="space-y-4">
            <FormField
              label="Name"
              required
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="e.g. Monthly Income Statement"
            />
            <FormField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              placeholder="Optional"
            />

            <div>
              <Label>Schedule</Label>
              <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <NativeSelect
                  value={freq}
                  onChange={(v) => setFreq(v as Freq)}
                  options={[
                    { value: 'daily', label: 'Every day' },
                    { value: 'weekdays', label: 'Every weekday (Mon–Fri)' },
                    { value: 'weekly', label: 'Every week' },
                    { value: 'monthly', label: 'Every month' },
                    { value: 'quarterly', label: 'Every quarter' },
                    { value: 'yearly', label: 'Every year' },
                  ]}
                />
                <div>
                  {freq === 'weekly' ? (
                    <NativeSelect
                      value={String(dow)}
                      onChange={(v) => setDow(parseInt(v, 10))}
                      options={[
                        { value: '1', label: 'Monday' },
                        { value: '2', label: 'Tuesday' },
                        { value: '3', label: 'Wednesday' },
                        { value: '4', label: 'Thursday' },
                        { value: '5', label: 'Friday' },
                        { value: '6', label: 'Saturday' },
                        { value: '0', label: 'Sunday' },
                      ]}
                    />
                  ) : null}
                  {freq === 'yearly' ? (
                    <div className="grid grid-cols-2 gap-2">
                      <NativeSelect
                        value={String(month)}
                        onChange={(v) => setMonth(parseInt(v, 10))}
                        options={Array.from({ length: 12 }, (_, i) => ({
                          value: String(i + 1),
                          label: [
                            'January', 'February', 'March', 'April', 'May', 'June',
                            'July', 'August', 'September', 'October', 'November', 'December',
                          ][i]!,
                        }))}
                      />
                      <NativeSelect
                        value={String(dom)}
                        onChange={(v) => setDom(parseInt(v, 10))}
                        options={Array.from({ length: 28 }, (_, i) => ({
                          value: String(i + 1),
                          label: `Day ${i + 1}`,
                        }))}
                      />
                    </div>
                  ) : null}
                  {(freq === 'monthly' || freq === 'quarterly') ? (
                    <NativeSelect
                      value={String(dom)}
                      onChange={(v) => setDom(parseInt(v, 10))}
                      options={Array.from({ length: 28 }, (_, i) => ({
                        value: String(i + 1),
                        label: `Day ${i + 1}`,
                      }))}
                    />
                  ) : null}
                </div>
                <Input
                  type="time"
                  value={`${pad(hour)}:${pad(minute)}`}
                  onChange={(e) => {
                    const [h, m] = e.currentTarget.value.split(':')
                    setHour(parseInt(h ?? '0', 10) || 0)
                    setMinute(parseInt(m ?? '0', 10) || 0)
                  }}
                />
              </div>
              <div className="mt-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">
                {describeCron(cron)} - cron: {cron} (timezone: {tz})
              </div>
            </div>

            <div>
              <Label>Timezone</Label>
              <NativeSelect
                value={tz}
                onChange={(v) => setTz(v)}
                options={Array.from(new Set([defaultTz, ...COMMON_TZ])).map((t) => ({
                  value: t,
                  label: t,
                }))}
              />
            </div>

            <div>
              <Label>Report</Label>
              <NativeSelect
                value={reportType}
                onChange={(v) => setReportType(v)}
                options={[
                  { value: 'generate_income_statement', label: 'Income Statement' },
                  { value: 'generate_balance_sheet', label: 'Balance Sheet' },
                  { value: 'generate_cash_flow', label: 'Cash Flow Statement' },
                  { value: 'generate_trial_balance', label: 'Trial Balance' },
                ]}
              />
            </div>

            <div>
              <Label>Period</Label>
              <NativeSelect
                value={dateRange}
                onChange={(v) => setDateRange(v)}
                options={[
                  { value: 'previous_month', label: 'Previous month' },
                  { value: 'previous_quarter', label: 'Previous quarter' },
                  { value: 'ytd', label: 'Year to date' },
                  { value: 'last_30_days', label: 'Last 30 days' },
                  { value: 'last_7_days', label: 'Last 7 days' },
                  { value: 'custom', label: 'Custom range…' },
                ]}
              />
              {dateRange === 'custom' ? (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <FormField
                    label="From"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.currentTarget.value)}
                  />
                  <FormField
                    label="To"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.currentTarget.value)}
                  />
                </div>
              ) : null}
            </div>

            <FormField
              label="Email recipients (comma-separated)"
              control={
                <Textarea
                  value={emails}
                  onChange={(e) => setEmails(e.currentTarget.value)}
                  placeholder="finance@company.com, cfo@…"
                />
              }
            />
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={notify}
                onCheckedChange={(v) => setNotify(v === true)}
              />
              Also create an in-app notification for every tenant member.
            </label>

            <div>
              <Label>Visibility</Label>
              <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <VisOption
                  icon={<Lock className="size-5" aria-hidden />}
                  label="Only me"
                  desc="Private to you"
                  selected={visibility === 'private'}
                  onClick={() => setVisibility('private')}
                />
                <VisOption
                  icon={<Users className="size-5" aria-hidden />}
                  label="Whole team"
                  desc="Everyone in this workspace"
                  selected={visibility === 'tenant'}
                  onClick={() => setVisibility('tenant')}
                />
                <VisOption
                  icon={<Target className="size-5" aria-hidden />}
                  label="Specific roles"
                  desc="Pick roles or members"
                  selected={visibility === 'shared'}
                  onClick={() => setVisibility('shared')}
                />
              </div>
            </div>

            {visibility === 'shared' ? (
              <RoleMemberSharePicker
                value={shareValue}
                onChange={setShareValue}
                currentUserId={currentUserId}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  )
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      className={cn(
        '-mb-px h-auto rounded-none border-b-2 px-4 py-2 text-sm font-semibold transition-colors hover:bg-transparent',
        active
          ? 'border-primary text-primary hover:text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </Button>
  )
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </label>
  )
}

function NativeSelect({
  value,
  onChange,
  options,
}: {
  value: string
  onChange: (v: string) => void
  options: Array<{ value: string; label: string }>
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      className="flex h-10 w-full rounded-sm border border-input bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

function VisOption({
  icon,
  label,
  desc,
  selected,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  desc: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      className={cn(
        'flex h-auto flex-col items-center gap-1 rounded-md border-2 p-3 text-center transition',
        selected ? 'border-primary bg-primary/10 hover:bg-primary/15' : 'border-input hover:border-primary/40',
      )}
    >
      <span className="text-primary">{icon}</span>
      <span className="text-sm font-semibold">{label}</span>
      <span className="text-xs text-muted-foreground">{desc}</span>
    </Button>
  )
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return 'Failed to save workflow.'
}
